import type { SchemaUserResolver_Get, SchemaUserResolver_GetRepresentedUsers } from 'cpool-tables/backend-resolvers';
import { isAbsenceActiveAt } from 'cpool-utils';

export type RepresentedUser = SchemaUserResolver_GetRepresentedUsers[number];
export type AdviserAbsenceEntry = NonNullable<RepresentedUser['absence']>[number];

type UserWithRepresentation = Pick<
    NonNullable<SchemaUserResolver_Get>,
    'absence' | 'delegateAppointedAt' | 'delegateAppointedById' | 'delegateId' | 'firstName' | 'id' | 'lastName'
>;

export type EndRepresentation =
    | {
          appointedAt: Date;
          appointedById: string;
          delegateId: string;
          kind: 'delegation';
      }
    | {
          absenceFrom: Date;
          absenceUntil: Date;
          appointerId: string;
          delegateId: string;
          eventId: string;
          kind: 'absence';
      };

const absenceTime = (value: Date | string) => (value instanceof Date ? value : new Date(value)).getTime();

export const isAbsenceActiveNow = (absence: AdviserAbsenceEntry, nowMs: number) => isAbsenceActiveAt(absence, nowMs);

export const isAbsenceFuture = (absence: AdviserAbsenceEntry, nowMs: number) =>
    absenceTime(absence.absenceFrom) > nowMs;

// not yet over: either active right now or starting in the future
export const isAbsenceUpcoming = (absence: AdviserAbsenceEntry, nowMs: number) =>
    absenceTime(absence.absenceUntil) > nowMs;

export const hasActiveAbsenceNow = (item: RepresentedUser, nowMs: number) =>
    Boolean(item.absence?.some((a) => isAbsenceActiveNow(a, nowMs)));

export const hasManualDelegation = (user: UserWithRepresentation) =>
    Boolean(user.delegateId && user.delegateAppointedById);

export const getActiveAbsenceEntry = (user: UserWithRepresentation, nowMs: number = Date.now()) =>
    user.absence?.find((absence) => isAbsenceActiveNow(absence, nowMs));

export const getActiveEndRepresentation = (
    user: UserWithRepresentation,
    nowMs: number = Date.now(),
): EndRepresentation | undefined => {
    const activeAbsence = getActiveAbsenceEntry(user, nowMs);
    if (activeAbsence) {
        return {
            absenceFrom: activeAbsence.absenceFrom,
            absenceUntil: activeAbsence.absenceUntil,
            appointerId: user.id,
            delegateId: activeAbsence.delegateId,
            eventId: activeAbsence.eventId,
            kind: 'absence',
        };
    }

    if (hasManualDelegation(user) && user.delegateId && user.delegateAppointedById && user.delegateAppointedAt) {
        return {
            appointedAt: user.delegateAppointedAt,
            appointedById: user.delegateAppointedById,
            delegateId: user.delegateId,
            kind: 'delegation',
        };
    }

    return undefined;
};

// a manual delegation (e.g. set by the team leader) has no end date and is always active
export const hasDelegation = (item: RepresentedUser) => Boolean(item.delegation);

export const hasActiveRepresentationNow = (item: RepresentedUser, nowMs: number) =>
    hasDelegation(item) || hasActiveAbsenceNow(item, nowMs);

export const sortRepresentedUsersByActiveAbsence = (users: RepresentedUser[], nowMs: number = Date.now()) =>
    [...users].sort((a, b) => {
        const aActive = hasActiveRepresentationNow(a, nowMs);
        const bActive = hasActiveRepresentationNow(b, nowMs);
        if (aActive === bActive) {
            return 0;
        }
        return aActive ? -1 : 1;
    });
